import React, {useState, useContext, useEffect} from "react";
import {ArrowPathIcon, CheckIcon, EyeIcon} from "@heroicons/react/20/solid";
import Button from "../../ui-components/Button";
import {CheckCircleIcon, EyeSlashIcon, PlusIcon, TrashIcon, XMarkIcon} from "@heroicons/react/24/solid";
import {FormattedMessage, useIntl} from "react-intl";
import Input from "../../ui-components/Input";
import Textarea from "../../ui-components/Textarea";
import {PencilSquareIcon} from "@heroicons/react/24/outline";
import Collapsible from "../../ui-components/Collapsible";
import PrivateImage from "../PrivateImage";
import WorksiteNotesSection from "../WorksiteNotesSection";
import CountryContext from "../../CountryContext";
import AuthenticationService from "../../services/AuthenticationService";
import ConfirmDelete from "../ConfirmDelete";
import Modal from "../../ui-components/Modal";
import ImageUploader from "./ImageUploader";
import useCountryAxiosInstance from "../../hooks/useCountryAxiosInstance";
import {NotificationsContext} from "../../ui-components/Notifications";
import UserNotificationsContext from "../../UserNotificationsContext";
import Badge from "../../ui-components/Badge";
import { ReadMore } from "../../ui-components/ReadMore";
import NotificationPing from "../../ui-components/NotificationPing";


const WorksiteTaskActivity = ({
    title,
    description,
    editable,
    canMarkAsDone=true,
    viewOnly=false,
    onEditComplete = () => {},
    skipped,
    onSkip = () => {},
    checked,
    onCheck = () => {},
    deleted,
    onDelete = () => {},
    completedBy,
    completedOn,
    createdBy,
    isDraft,
    worksite,
    activityId,
    children,
    skippable = true,
    confirmDelete = true,
    note_id = undefined,
    activityType = undefined,
}) => {

  // hooks
  const intl = useIntl();
  const countryApiRequest = useCountryAxiosInstance();
  // context
  const { country } = useContext(CountryContext);
  const { push } = useContext(NotificationsContext);
  const { userNotifications, setUserNotifications } = useContext(
    UserNotificationsContext
  );

  // state
  const [editMode, setEditMode] = useState(!activityId && editable && !(title || description));
  const [content, setContent] = useState({title, description});
  const [pictures, setPictures] = useState(null);
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [modalPicture, setModalPicture] = useState(null);
  const [commentsCount, setCommentsCount] = useState(null);
  const [hasNotification, setHasNotification] = useState(false);

  // utilities
  const userId = AuthenticationService.getUserData().id;
  const onPictureAdd = async (url) => {
    const image = await countryApiRequest.post(`/images`, {
      val_type: 'worksite_activity',
      id_worksite: worksite.id,
      id_worksites_activity: activityId,
      val_url: url,
      val_description: null,
      r_perm: ['public'],
      w_perm: ['public'],
    });
    setPictures((prev) => [...prev, image]);
  };
  const onPictureDelete = async (id) => {
    setPictures((prev) => prev.filter((img) => img.id !== id));
    await countryApiRequest.delete(`/images/${id}`);
  };

  useEffect(() => {
    if (!userNotifications || !activityId) {
      return;
    }
    const activitiesNotifications = userNotifications
      ?.filter(
        (n) =>
          n.val_type === "worksites_tasks_activities" &&
          parseInt(n.cod_task_activity) === parseInt(activityId)
      )
      ?.map((n) => n.id);
    if (activitiesNotifications.length) {
      setUserNotifications((prev) =>
        prev.filter((n) => !activitiesNotifications.includes(n.id))
      );
      // set badge notification, but given we are clearing notification context here
      // do not reset it
      if (!hasNotification) {
        setHasNotification(() => true);
      }
    }
  }, [activityId, setUserNotifications, userNotifications, hasNotification]);
  

  useEffect(() => {
    const params = {id_worksites_activity: activityId, asc: 'created_at'};
    countryApiRequest.get(`/images/all`, {params})
      .then(setPictures)
      .catch((e) => {
        push({type: 'error', title: intl.formatMessage({id: 'server_error'}) })
        throw e;
      })
  }, [activityId, countryApiRequest, intl, push]);


  const renderActionButton = (Icon, style, title, onClick, disabled=false) => (
    <div className="text-center">
      <Button
        type="button"
        paddingCls={title ? 'px-3 py-1' : 'p-1'}
        textCls="text-xs"
        styleType={style}
        onClick={onClick}
        disabled={disabled}
      >
        {!!Icon ? <Icon className="w-3" /> : <div className="w-3 h-3" />}
        {!!title && <FormattedMessage id={title} />}
      </Button>
    </div>
  )

  const actions = editMode
    ? <>
      {renderActionButton(CheckIcon, "default", "save", () => {
        setEditMode(false);
        onEditComplete(content);
      })}
      {renderActionButton(XMarkIcon, "empty", "cancel", () => {
        setEditMode(false);
        setContent({title, description});
      })}
    </>
    : <>
      {canMarkAsDone && !viewOnly && !skipped && !!onCheck && renderActionButton(checked ? XMarkIcon : CheckIcon, checked ? "default" : "darkGreen", checked ? "mark_undone" : "mark_done", () => { onCheck(!checked) })}
      {editable && renderActionButton(PencilSquareIcon, "empty", "edit", () => { setEditMode(true); })}
      {!viewOnly && (skippable && (skipped
        ? renderActionButton(EyeIcon, "empty", "show", () => onSkip(false))
        : renderActionButton(EyeSlashIcon, "empty", "skip", () => onSkip(true))
      ))}
      {editable && !confirmDeleteOpen && renderActionButton(TrashIcon, "empty", "delete", () => confirmDelete ? setConfirmDeleteOpen(true) : onDelete(true) )}
      {confirmDeleteOpen && <div className="my-2 w-full"><ConfirmDelete
        onConfirm={() => onDelete(true)}
        showaskConfirm={true}
        onDismiss={() => setConfirmDeleteOpen(false)}
        confirmText={intl.formatMessage({ id: "confirm" })}
        dismissText={intl.formatMessage({ id: "cancel" })}
        showIcon={false}
      /></div>}
    </>;

  const body = editMode
    // -----
    // -----   EDIT MODE
    // -----
    ? <>
      <p className="text-xs font-medium">
        <FormattedMessage id="title" />
      </p>
      <Input
        type="text"
        onChange={(e) => setContent((prev) => ({...prev, title: e.target.value}))}
        value={content.title || ""}
        hideBottomMargin={true}
      />
      <p className="text-xs font-medium mt-2">
        <FormattedMessage id="description" />
      </p>
      <Textarea
        value={content.description || ""}
        onChange={(e) => setContent((prev) => ({...prev, description: e.target.value}))}
        rows="3"
        textCls="text-sm"
        paddingCls="px-2.5 py-2"
        marginCls="mt-1"
        hideBottomMargin={true}
      />
    </>
    // -----
    // -----   STATIC MODE
    // -----
    : <>
      <h2 className={"text-sm mb-2 -mt-0.5 " + (skipped ? "line-through opacity-40" : "")}>
        {title}
      </h2>
      {createdBy && <p className="text-xs mt-0.5 mb-2">
        <FormattedMessage id="custom_activity_created_by" values={{user: <b>{createdBy}</b>}} />
      </p>}
      {!!description && <div className="mt-1">
        <ReadMore
          styleType="white"
        >
          {description}
        </ReadMore>
      </div>}
      {!isDraft && <>
        <div className="my-3">
          <Collapsible
            title={<><FormattedMessage id="discussion" />{` (${commentsCount ?? 0})`}</>}
            classNames="my-1.5"
            disabled={true}
            defaultOpen={!!note_id}
          >
            <WorksiteNotesSection
              worksite={worksite}
              activityId={activityId}
              infiniteScroll={false}
              paginationBuffer={5}
              viewOnly={viewOnly || skipped}
              setCommentsCount={setCommentsCount}
              highlightedNoteId={note_id}
              defaultSortMode="asc"
            />
          </Collapsible>
          <Collapsible
            title={<><FormattedMessage id="attachments" />{` (${pictures?.length ?? 0})`}</>}
            classNames="my-1.5"
          >
            <div className="flex flex-wrap gap-3">
              {pictures?.map((img, idx) => (
                <div key={idx} className="relative">
                  <div className="w-24 cursor-pointer hover:opacity-80" onClick={() => { setModalPicture(img.val_url); }}>
                    <PrivateImage
                      image_url={img.val_url}
                      tag="div"
                      className="w-full flex flex-col bg-white rounded-lg shadow bg-cover"
                    />
                  </div>
                  <div
                    className="absolute flex items-center justify-center text-white bg-red-500 rounded-full w-5 h-5 -right-2 -bottom-2 cursor-pointer hover:bg-red-700"
                    onClick={async () => { await onPictureDelete(img.id); }}
                  >
                    <XMarkIcon className="w-3" />
                  </div>
                </div>
              ))}
              {!viewOnly && !skipped && (
                <ImageUploader
                  prefix={`images/${country.code}/tasks/activities/${userId}`}
                  bucket="task_images"
                  uploading={uploadingPicture}
                  setUploading={setUploadingPicture}
                  onUploadComplete={onPictureAdd}
                >
                  <div
                    className={`w-24 flex items-center justify-center bg-gray-200 rounded-lg text-gray-400 ${uploadingPicture ? 'cursor-not-allowed pointer-events-none' : 'cursor-pointer'} hover:bg-gray-300 hover:text-gray-500`}
                    style={{aspectRatio: '16 / 10'}}
                  >
                    {uploadingPicture
                      ? <ArrowPathIcon className="w-6 animate-spin" />
                      : <PlusIcon className="w-6" />
                    }
                  </div>
                </ImageUploader>
              )}
            </div>
          </Collapsible>
        </div>
        {completedBy && <div className="flex items-center gap-1 mt-3 text-xs">
          <CheckCircleIcon className="w-4" />
          <FormattedMessage
            id="completed_by_on"
            values={{
              user: <span className="font-semibold">{completedBy}</span>,
              dt: new Intl.DateTimeFormat(undefined, {day: '2-digit', month: '2-digit', year: 'numeric'}).format(completedOn)
            }}
          />
        </div>}
      </>}
    </>;

  return <>
    <div className={`relative px-3 py-4 pt-9 flex gap-3 items-start rounded-xl bg-gray-100 shadow-md ${deleted ? "opacity-40" : ""}`}>
      <div className="absolute pointer-events-none top-2 right-2 flex items-center gap-2.5">
        {!!hasNotification && <NotificationPing />}
        {!!activityType && (
          <Badge
            type={activityType === "custom" ? "primary" : "info"}
            text={intl.formatMessage({ id: `activity_type_${activityType}` })}
            textSize="text-xs"
          />
        )}
      </div>
      {!!onCheck && !skipped && <div className="flex">
        <div className="pointer-events-none">
          {renderActionButton(
            skipped ? XMarkIcon : (checked ? CheckIcon : null),
            skipped ? "gray" : (checked ? "darkGreen" : "greenWhite"),
            null,
            () => {},
            skipped,
          )}
        </div>
      </div>}
      {!!skipped && <div className="pl-6"></div>}
      <div className={`flex-1 ${!onCheck ? 'pl-2' : ''}`}>
        <div>
          {body}
        </div>
        <hr className="my-3" />
        <div className="flex flex-wrap gap-2">
          {actions}
        </div>
      </div>
      <div className="absolute left-0 top-1/2">
        {children}
      </div>
    </div>
    <Modal
      opened={!!modalPicture}
      onExit={() => { setModalPicture(null); }}
    >
      <PrivateImage image_url={modalPicture} tag="img" fixedAspectRatio={false} />
    </Modal>
  </>
}

export default WorksiteTaskActivity;